import Label from "@components/Label";
import { Button, Grid, Stack, Typography } from "@mui/material";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { TaxRatesEntity } from "src/@types/catalog/taxRates";
import {
  defaultItemValues,
  IItemDefaults,
} from "../../utils/defaultItemValues";
import { AddLineItemsDialog, ISelectedLineItem } from "./AddLineItemsDialog";
import { LineItemsTable } from "./LineItemsTable";

interface ILineItemsProps {
  translate: (text: any, options?: any) => string;
  taxRates: TaxRatesEntity[] | null;
  onTaxesRefresh: () => void;
  selectedComplianceStandard?: string | null;
}

export const LineItems = ({
  translate,
  taxRates,
  onTaxesRefresh,
  selectedComplianceStandard,
}: ILineItemsProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<any>();
  const {
    fields: lineItems,
    remove: itemRemove,
    update: itemUpdate,
    append: itemAppend,
  } = useFieldArray({ control, name: "items" });

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<ISelectedLineItem>({
    index: null,
    defaultItemValues,
  });

  const toggle = () => setIsOpen((v) => !v);

  const handleEdit = (index: number | null, item: IItemDefaults) => {
    setSelected({ index, defaultItemValues: item });
    toggle();
  };

  const errorChips = Object.entries(errors ?? {})
    ?.filter(([key]) => key === "items")
    .map(([field, error]) => ({
      field,
      message: (error as any)?.message || translate(field),
    }));

  return (
    <Grid container spacing={1}>
      <AddLineItemsDialog
        translate={translate}
        taxRates={taxRates}
        onTaxesRefresh={onTaxesRefresh}
        selectedComplianceStandard={selectedComplianceStandard}
        itemUpdate={itemUpdate as any}
        itemAppend={itemAppend as any}
        isShowModal={isOpen}
        showModalMethod={toggle}
        initialValues={selected}
      />
      <Grid item xs={12}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6">{translate("line_items")}</Typography>
          {errorChips.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {errorChips.map((error) => (
                <Label key={error.field} variant="ghost" color="error">
                  {translate(error.message)}
                </Label>
              ))}
            </Stack>
          )}
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <LineItemsTable
          translate={translate}
          lineItems={lineItems as any}
          itemRemove={itemRemove}
          handleUpdateLineItem={(i, item) => handleEdit(i, item)}
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          size="small"
          variant="text"
          startIcon={<Plus />}
          onClick={() => handleEdit(null, defaultItemValues)}
          sx={{ fontSize: "14px" }}
        >
          {translate("add_line_item")}
        </Button>
      </Grid>
    </Grid>
  );
};
